import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { decode } from "../Services/DecodeToken";

export const DEFAULT_WIDGET_ORDER = [
  "productCategory",
  "productCategoryVolume",
  "user",
  "employeeVolume",
  "requesterVolume",
  "requesterCost",
  "location",
  "locationVolume",
  "poJobCost",
] as const;

export type WidgetId = (typeof DEFAULT_WIDGET_ORDER)[number] | string;

const STORAGE_KEY_PREFIX = "aimsWidgetPreferences";
const LEGACY_GLOBAL_KEY = "aimsWidgetPreferences";
const LEGACY_VISIBLE_KEY = "visibleWidgets";

type StoredPreferences = {
  order: string[];
  visible: string[];
};

type WidgetPreferencesContextType = {
  /** All known widgets in the user's preferred display order */
  widgetOrder: string[];
  /** Currently visible widgets, in display order */
  visibleWidgets: string[];
  updateVisibleWidgets: (widgets: string[]) => void;
  /** Reorder the full widget list (Settings page / homepage drag) */
  updateWidgetOrder: (order: string[]) => void;
  /** Set both order and visibility together */
  updatePreferences: (order: string[], visible: string[]) => void;
  setWidgetVisible: (widgetId: string, visible: boolean) => void;
  moveWidget: (widgetId: string, direction: "up" | "down") => void;
  /** Unix ms of last successful persist (0 if never saved this session) */
  lastSavedAt: number;
  ready: boolean;
};

const WidgetPreferencesContext = createContext<WidgetPreferencesContextType | undefined>(
  undefined,
);

const knownWidgetIds = () => [...DEFAULT_WIDGET_ORDER];

const defaultPreferences = (): StoredPreferences => ({
  order: knownWidgetIds(),
  visible: knownWidgetIds(),
});

const canUseLocalStorage = (): boolean => {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    const probe = "__aims_widget_prefs_probe__";
    localStorage.setItem(probe, probe);
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

const getUserId = (): string | undefined => {
  try {
    const token = decode() as { userId?: string } | null;
    return token?.userId ? String(token.userId) : undefined;
  } catch {
    return undefined;
  }
};

const storageKeyForUser = (userId?: string) =>
  userId ? `${STORAGE_KEY_PREFIX}_${userId}` : LEGACY_GLOBAL_KEY;

export const normalizeOrder = (order: string[]): string[] => {
  const known = knownWidgetIds();
  const knownSet = new Set<string>(known);
  const cleaned = order.filter((id, index, arr) => knownSet.has(id) && arr.indexOf(id) === index);
  const missing = known.filter((id) => !cleaned.includes(id));
  return [...cleaned, ...missing];
};

export const normalizeVisible = (order: string[], visible: string[]): string[] => {
  const orderSet = new Set(order);
  const cleaned = visible.filter((id, index, arr) => orderSet.has(id) && arr.indexOf(id) === index);
  if (cleaned.length === 0) {
    return order.slice(0, 1);
  }
  // Keep visible list in the same relative order as `order`
  return order.filter((id) => cleaned.includes(id));
};

const parseStored = (raw: string | null): StoredPreferences | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.order) && Array.isArray(parsed.visible)) {
      const order = normalizeOrder(parsed.order);
      return {
        order,
        visible: normalizeVisible(order, parsed.visible),
      };
    }
  } catch {
    // ignore corrupt payload
  }
  return null;
};

const parseLegacyVisible = (raw: string | null): StoredPreferences | null => {
  if (!raw) return null;
  try {
    const parsedLegacy = JSON.parse(raw);
    if (Array.isArray(parsedLegacy)) {
      const order = normalizeOrder(parsedLegacy);
      const visible = normalizeVisible(order, parsedLegacy);
      return { order, visible };
    }
  } catch {
    // ignore corrupt payload
  }
  return null;
};

export const loadPreferences = (userId?: string): StoredPreferences => {
  const defaults = defaultPreferences();
  if (!canUseLocalStorage()) return defaults;

  try {
    // Prefer per-user key, then migrate from legacy global / visibility-only keys
    const userKey = storageKeyForUser(userId);
    const fromUser = parseStored(localStorage.getItem(userKey));
    if (fromUser) return fromUser;

    if (userId) {
      const fromGlobal = parseStored(localStorage.getItem(LEGACY_GLOBAL_KEY));
      if (fromGlobal) {
        savePreferences(fromGlobal, userId);
        return fromGlobal;
      }
    }

    const fromLegacyVisible = parseLegacyVisible(localStorage.getItem(LEGACY_VISIBLE_KEY));
    if (fromLegacyVisible) {
      savePreferences(fromLegacyVisible, userId);
      return fromLegacyVisible;
    }
  } catch (error) {
    console.warn("Error reading widget preferences from localStorage:", error);
  }

  return defaults;
};

export const savePreferences = (
  prefs: StoredPreferences,
  userId?: string,
): boolean => {
  if (!canUseLocalStorage()) return false;
  try {
    const normalized: StoredPreferences = {
      order: normalizeOrder(prefs.order),
      visible: normalizeVisible(normalizeOrder(prefs.order), prefs.visible),
    };
    localStorage.setItem(storageKeyForUser(userId), JSON.stringify(normalized));
    // Keep legacy keys in sync for older readers / same-browser fallback
    localStorage.setItem(LEGACY_GLOBAL_KEY, JSON.stringify(normalized));
    localStorage.setItem(LEGACY_VISIBLE_KEY, JSON.stringify(normalized.visible));
    return true;
  } catch (error) {
    console.warn("Error saving widget preferences to localStorage:", error);
    return false;
  }
};

export const WidgetPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const userId = getUserId();

  const [widgetOrder, setWidgetOrder] = useState<string[]>(() =>
    loadPreferences(getUserId()).order,
  );
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>(() =>
    loadPreferences(getUserId()).visible,
  );
  const [lastSavedAt, setLastSavedAt] = useState(0);
  const [ready, setReady] = useState(true);
  const [hydratedUserId, setHydratedUserId] = useState<string | undefined>(() =>
    getUserId(),
  );

  // Re-load when the authenticated user changes (e.g. after login redirect)
  useEffect(() => {
    if (userId === hydratedUserId) return;
    const prefs = loadPreferences(userId);
    setWidgetOrder(prefs.order);
    setVisibleWidgets(prefs.visible);
    setHydratedUserId(userId);
    setReady(true);
  }, [userId, hydratedUserId]);

  const persist = useCallback(
    (order: string[], visible: string[]) => {
      const nextOrder = normalizeOrder(order);
      const nextVisible = normalizeVisible(nextOrder, visible);
      setWidgetOrder(nextOrder);
      setVisibleWidgets(nextVisible);
      const ok = savePreferences({ order: nextOrder, visible: nextVisible }, userId);
      if (ok) {
        setLastSavedAt(Date.now());
      }
      return ok;
    },
    [userId],
  );

  const updateVisibleWidgets = useCallback(
    (widgets: string[]) => {
      // Treat the provided list as the new visible set in that order,
      // and move those ids to the front of the overall order while
      // preserving relative order of remaining (hidden) widgets.
      const visible = widgets.filter((id, index, arr) => arr.indexOf(id) === index);
      const remaining = widgetOrder.filter((id) => !visible.includes(id));
      persist([...visible, ...remaining], visible);
    },
    [persist, widgetOrder],
  );

  const updateWidgetOrder = useCallback(
    (order: string[]) => {
      persist(order, visibleWidgets);
    },
    [persist, visibleWidgets],
  );

  const updatePreferences = useCallback(
    (order: string[], visible: string[]) => {
      persist(order, visible);
    },
    [persist],
  );

  const setWidgetVisible = useCallback(
    (widgetId: string, visible: boolean) => {
      if (visible) {
        if (visibleWidgets.includes(widgetId)) return;
        persist(widgetOrder, [...visibleWidgets, widgetId]);
        return;
      }

      if (visibleWidgets.length <= 1) return;
      persist(
        widgetOrder,
        visibleWidgets.filter((id) => id !== widgetId),
      );
    },
    [persist, visibleWidgets, widgetOrder],
  );

  const moveWidget = useCallback(
    (widgetId: string, direction: "up" | "down") => {
      const index = widgetOrder.indexOf(widgetId);
      if (index === -1) return;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= widgetOrder.length) return;

      const next = [...widgetOrder];
      [next[index], next[target]] = [next[target], next[index]];
      persist(next, visibleWidgets);
    },
    [persist, visibleWidgets, widgetOrder],
  );

  const value = useMemo(
    () => ({
      widgetOrder,
      visibleWidgets,
      updateVisibleWidgets,
      updateWidgetOrder,
      updatePreferences,
      setWidgetVisible,
      moveWidget,
      lastSavedAt,
      ready,
    }),
    [
      widgetOrder,
      visibleWidgets,
      updateVisibleWidgets,
      updateWidgetOrder,
      updatePreferences,
      setWidgetVisible,
      moveWidget,
      lastSavedAt,
      ready,
    ],
  );

  return (
    <WidgetPreferencesContext.Provider value={value}>
      {children}
    </WidgetPreferencesContext.Provider>
  );
};

export const useWidgetPreferences = () => {
  const context = useContext(WidgetPreferencesContext);
  if (!context) {
    throw new Error("useWidgetPreferences must be used within a WidgetPreferencesProvider");
  }
  return context;
};
